import React, { useRef, useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Typography,
  makeStyles,
  Hidden,
  Tooltip
} from '@material-ui/core';
import OnlineIndicator from 'src/components/OnlineIndicator';
import PropTypes from 'prop-types';
import { setActiveDistributor } from 'src/store/actions/distributorActions';
import { setActivePermissions } from 'src/store/actions/accountActions';
import getInitials from 'src/utils/getInitials';
import { useHistory } from 'react-router';

const useStyles = makeStyles((theme) => ({
  popover: {
    width: 320,
    padding: theme.spacing(2)
  },
  list: {
    padding: theme.spacing(1, 3)
  },
  listItemText: {
    marginRight: theme.spacing(1)
  },
  lastActivity: {
    whiteSpace: 'nowrap'
  }
}));

function DistributorRow({ distributor, activeDistributor, onClick }) {
  const classes = useStyles();

  const handleRowClick = useCallback(() => onClick(distributor), [onClick, distributor]);

  return (
    <ListItem disableGutters key={distributor.id} onClick={handleRowClick}>
      <ListItemAvatar>
        <Avatar alt="" src={distributor.avatar}>
          {getInitials(distributor.name)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        className={classes.listItemText}
        disableTypography
        primary={
          <Link color="textPrimary" component={RouterLink} display="block" underline="none" noWrap to="#" variant="h6">
            {distributor.name}
          </Link>
        }
      />
      {distributor.id === activeDistributor.id && <OnlineIndicator size="medium" status="online" />}
    </ListItem>
  );
}

DistributorRow.propTypes = {
  distributor: PropTypes.object.isRequired,
  activeDistributor: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
};

function Distributors() {
  const classes = useStyles();
  const { push } = useHistory();
  const ref = useRef(null);
  const dispatch = useDispatch();
  const { distributors, activeDistributor } = useSelector((state) => state.distributor);
  const { user, distributorRoles } = useSelector((state) => state.account);
  const distributorsLength = distributors?.length;
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    if (distributorsLength > 1) {
      setIsOpen(true);
    }
  }, [distributorsLength]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSwitchDistributor = useCallback(
    async (distributor) => {
      setIsOpen(false);
      dispatch(setActiveDistributor(distributor));
      dispatch(setActivePermissions(distributor));
      push('/app');
    },
    [dispatch, push]
  );

  // Only show for EXTERNAL users with distributorRoles
  if (user?.type !== 'EXTERNAL' || !distributorRoles?.length) {
    return null;
  }

  if (!distributors) {
    return null;
  }

  return (
    <>
      <Tooltip title="Distributors">
        <Box
          display="flex"
          alignItems="center"
          flexDirection="row"
          justifyContent="space-between"
          marginLeft="1.5em"
          marginRight="1.5em"
          onClick={handleOpen}
          ref={ref}
        >
          <Avatar alt="" src={activeDistributor.avatar} style={{ marginRight: '4px', width: 32, height: 32 }}>
            {getInitials(activeDistributor.name)}
          </Avatar>
          <Hidden smDown>
            <Typography variant="h6" color="inherit">
              {`${activeDistributor.name}`}
            </Typography>
          </Hidden>
        </Box>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        classes={{ paper: classes.popover }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <Typography variant="h4" color="textPrimary">
          Distributors
        </Typography>
        <Box mt={2}>
          <List disablePadding>
            {distributors?.map((distributor) => (
              <DistributorRow
                distributor={distributor}
                activeDistributor={activeDistributor}
                key={distributor.id}
                onClick={handleSwitchDistributor}
              />
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
}

export default Distributors;
